import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import vehiclesService, { VehicleHistorySnapshot } from "services/vehiclesService";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Hub, KeyValue } from "common/models";

interface VehicleHistoryProps {
  vehicleId: number;
  hubs: Hub[];
  types: KeyValue[];
  statuses: KeyValue[];
  outOfServiceReasons: KeyValue[];
}

interface FieldChange {
  label: string;
  oldValue: string;
  newValue: string;
}

interface ChangeEvent {
  timestamp: string | null;
  userName: string | null;
  isCurrent: boolean;
  changes: FieldChange[];
}

const lookupName = (list: { id: number; name?: string }[] | undefined, raw: string | null): string => {
  if (raw == null || raw === "") return "";
  const id = Number(raw);
  if (Number.isNaN(id)) return raw;
  return list?.find((x) => x.id === id)?.name ?? raw;
};

const formatBool = (v: boolean | null | undefined) =>
  v === true ? "Yes" : v === false ? "No" : "";

const formatString = (v: string | null | undefined) => (v == null ? "" : String(v).trim());

const formatNumber = (v: number | null | undefined) =>
  v == null ? "" : Number(v).toLocaleString();

const VehicleHistory: React.FC<VehicleHistoryProps> = ({
  vehicleId,
  hubs,
  types,
  statuses,
  outOfServiceReasons,
}) => {
  const [snapshots, setSnapshots] = useState<VehicleHistorySnapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicleId) return;
    setLoading(true);
    vehiclesService
      .getVehicleHistory(vehicleId)
      .then((data) => setSnapshots(data ?? []))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  const events = useMemo<ChangeEvent[]>(() => {
    if (snapshots.length === 0) return [];

    const fieldDefs: Array<{
      key: keyof VehicleHistorySnapshot;
      label: string;
      format: (v: any) => string;
    }> = [
      { key: "hubId", label: "Hub", format: (v) => lookupName(hubs as any, v == null ? null : String(v)) },
      { key: "vehicleTypeId", label: "Type", format: (v) => lookupName(types, v == null ? null : String(v)) },
      { key: "vehicleStatus", label: "Status", format: (v) => lookupName(statuses, v) },
      { key: "outOfServiceReason", label: "Out Of Service Reason", format: (v) => lookupName(outOfServiceReasons, v) },
      { key: "description", label: "Description", format: formatString },
      { key: "unitNumber", label: "Unit Number", format: formatNumber },
      { key: "plateNumber", label: "Plate Number", format: formatString },
      { key: "vin", label: "VIN", format: formatString },
      { key: "odometer", label: "Odometer", format: formatNumber },
      { key: "isActive", label: "Active", format: formatBool },
      { key: "isSuspended", label: "Suspended", format: formatBool },
      { key: "capacity", label: "Capacity", format: formatString },
      { key: "make", label: "Make", format: formatString },
      { key: "year", label: "Year", format: formatString },
      { key: "color", label: "Color", format: formatString },
      { key: "phoneInVehicle", label: "Phone In Vehicle", format: formatString },
      { key: "isSatelliteEquipped", label: "Satellite Equipped", format: formatBool },
      { key: "modemIpAddress", label: "Modem IP", format: formatString },
      { key: "modemEsnNumber", label: "Modem ESN", format: formatString },
      { key: "deviceSerialNumber", label: "Device Serial", format: formatString },
      { key: "cameraDeviceNumber", label: "Camera Device", format: formatString },
      { key: "modemSimNumber", label: "Modem SIM", format: formatString },
      { key: "fuelTankVolume", label: "Fuel Tank Volume", format: formatNumber },
      { key: "leaseCompany", label: "Lease Company", format: formatString },
      { key: "comments", label: "Comments", format: formatString },
    ];

    const result: ChangeEvent[] = [];
    for (let i = 1; i < snapshots.length; i++) {
      const newer = snapshots[i];
      const older = snapshots[i - 1];
      const changes: FieldChange[] = [];
      for (const def of fieldDefs) {
        const newRaw = newer[def.key];
        const oldRaw = older[def.key];
        const newStr = def.format(newRaw);
        const oldStr = def.format(oldRaw);
        if (newStr !== oldStr) {
          changes.push({ label: def.label, oldValue: oldStr, newValue: newStr });
        }
      }
      if (changes.length > 0) {
        result.push({
          timestamp: newer.timestamp,
          userName: newer.userName,
          isCurrent: newer.isCurrent,
          changes,
        });
      }
    }

    return result.reverse();
  }, [snapshots, hubs, types, statuses, outOfServiceReasons]);

  if (loading) {
    return (
      <MDBox p={3} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </MDBox>
    );
  }

  if (events.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="body2" color="text">
          No modification history is available for this vehicle.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={2}>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="Vehicle modification history">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 180 }}>When</TableCell>
              <TableCell sx={{ width: 180 }}>Who</TableCell>
              <TableCell>What changed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((e, idx) => (
              <TableRow key={idx} hover>
                <TableCell>
                  {e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}
                </TableCell>
                <TableCell>{e.userName ?? "—"}</TableCell>
                <TableCell>
                  {e.changes.map((c, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                      <b>{c.label}:</b>{" "}
                      <span style={{ color: "#b00020", textDecoration: "line-through" }}>
                        {c.oldValue || "(empty)"}
                      </span>{" "}
                      <span style={{ color: "#666" }}>→</span>{" "}
                      <span style={{ color: "#006400" }}>{c.newValue || "(empty)"}</span>
                    </div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
};

export default VehicleHistory;
